import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { IOAuth2Client } from "@sso/oauth2-clients/contracts";
import { Grid } from "@material-ui/core";
import {
  AutocompleteField,
  Button,
  GraphQLValidationErrors,
  SelectField,
  SelectFieldOption,
  TextField,
} from "@funcional/react-ui";
import { IGraphQLError } from "@common/contracts";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormattedDuration from "react-intl-formatted-duration";

const containerSpacing = 3;

const schema = yup.object().shape({
  name: yup.string().required().min(3, "o nome deve ter pelo menos 3 letras"),
  type: yup
    .string()
    .required()
    .oneOf(["ACCESS_TOKEN", "PKCE", "PASSWORD", "CLIENT_CREDENTIALS"]),
  tokenExpiration: yup
    .number()
    .min(3600, "o tempo mínimo é de uma hora (3600 segundos)")
    .nullable()
    .optional(),
  userId: yup
    .number()
    .min(1, "insira o ID do usuário fixado para este cliente")
    .nullable()
    .optional(),
  redirects: yup.array().of(yup.string().required()),
});

export default function OAuth2ClientForm({
  onSubmit,
  defaults,
  isLoading,
  graphQLErrors,
}: {
  onSubmit: SubmitHandler<FieldValues>;
  defaults?: IOAuth2Client;
  isLoading?: boolean;
  graphQLErrors?: IGraphQLError[];
}) {
  const form = useForm({
    resolver: yupResolver(schema),
  });
  const {
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = form;
  const watchType = watch("type", defaults?.type);
  const watchTokenExpiration = watch(
    "tokenExpiration",
    defaults?.tokenExpiration
  );
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-xl">
      <GraphQLValidationErrors graphQLErrors={graphQLErrors} />
      <Grid container spacing={containerSpacing}>
        <Grid item xs={12}>
          <TextField
            required
            name="name"
            label="Nome"
            control={control}
            defaultValue={defaults?.name}
            errorMessage={errors?.code?.message}
            helperText="Identifica qual é o sistema que irá autenticar usuários via SSO"
          />
        </Grid>

        <Grid item xs={4}>
          <SelectField
            required
            control={control}
            defaultValue={defaults?.type || ""}
            errorMessage={errors?.type?.message}
            name="type"
            id="type"
            label="Tipo"
            helperText="Tipo de fluxo de OAuth2"
            disabled={defaults?.type !== undefined}
          >
            <SelectFieldOption value="ACCESS_TOKEN">
              Access Token
            </SelectFieldOption>
            <SelectFieldOption value="PKCE">PKCE</SelectFieldOption>
            <SelectFieldOption value="CLIENT_CREDENTIALS">
              Client Credentials
            </SelectFieldOption>
            <SelectFieldOption value="PASSWORD">Password</SelectFieldOption>
          </SelectField>
        </Grid>

        <Grid item xs={8}>
          <div className="p-4 text-xs bg-gray-200 rounded">
            {watchType === "PKCE"
              ? "Utilizado em sistemas somente com front-end, sem um back-end, para seres humanos se autenticarem. Exemplos: aplicativos de celular (como o Funcional Card), sites em React (SPAs, como o Monitoro), aplicações desktop."
              : watchType === "ACCESS_TOKEN"
              ? "Utilizado em sistemas que possuem front-end e back-end, onde ocorre redirecionamento de tela e um usuário ser humano digita sua senha. Exemplos: sites com arquitetura MVC (como o Tradetools, Plant, etc.)."
              : watchType === "CLIENT_CREDENTIALS"
              ? "Utilizado para que um servidor (usuário máquina) se autentique via SSO como um usuário fixo específico, sem interação humana. Exemplo: quando o backend do Funcional Acesso se autentica como se fosse um administrador para gerenciar usuários no SSO."
              : watchType === "PASSWORD"
              ? "Utilizado para que um servidor repasse o login e senha e se autentique em nome de um ser humano. Exemplo: autenticação em API gateways (onde o usuário informa login e senha, mas o Gateway repassa para o SSO)."
              : "Selecione um tipo de cliente para saber mais sobre ele"}
          </div>
        </Grid>

        <Grid item xs={6}>
          <TextField
            type="number"
            name="tokenExpiration"
            label="Duração do token"
            helperText="Após quantos segundos expira o JWT"
            control={control}
            defaultValue={defaults?.tokenExpiration}
            errorMessage={errors?.tokenExpiration?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <div className="p-4 text-xs bg-gray-200 rounded">
            <FormattedDuration
              seconds={
                watchTokenExpiration && watchTokenExpiration > 0
                  ? watchTokenExpiration
                  : 86400
              }
              unitDisplay="short"
              format="{days} {hours} {minutes} {seconds}"
            />
          </div>
        </Grid>

        {watchType === "CLIENT_CREDENTIALS" ? (
          <Grid item xs={12}>
            <TextField
              type="number"
              name="userId"
              label="Usuário"
              helperText="ID do usuário fixado para este cliente"
              control={control}
              defaultValue={defaults?.userId}
              errorMessage={errors?.userId?.message}
              readOnly={defaults?.type !== undefined}
            />
          </Grid>
        ) : null}

        {watchType === "ACCESS_TOKEN" || watchType === "PKCE" ? (
          <Grid item xs={12}>
            <AutocompleteField
              freeSolo
              label="URLs de Redirecionamento"
              placeholder="Digite a URL e aperte enter"
              helperText="URLs de callback autorizadas para login com este cliente no SSO"
              name="redirects"
              control={control}
              options={(defaults?.redirects || []).map((url) => ({
                value: url,
                label: url,
              }))}
              defaultValue={(defaults?.redirects || []).map((url) => ({
                label: url,
                value: url,
              }))}
              errorMessage={
                errors?.redirects?.message ||
                ((errors?.redirects?.length || 0) > 0
                  ? errors?.redirects
                      ?.filter((item: any) => item !== null)
                      .map((item: any) => item.message)
                  : undefined)
              }
            />
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <Button color="primary" type="submit" disabled={isLoading}>
            Salvar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
